import React from 'react';
import { AlertCircle, Brain, Calendar, Car, Eye } from 'lucide-react';
import { Modal } from '../../common/Modal';
import JobPhotos from '../parts/JobPhotos';
import { getStatusColor, getPriorityColor } from '../parts/statusStyles';

// Extracted verbatim from JobManagement's JSX (Task 8).
// The orchestrator keeps the `show*` guard, so mount/unmount semantics are
// unchanged; this component is the modal's body only.
//
// isOpen is hardcoded true: the orchestrator's `showJobDetails &&` guard
// at the call site is what controls mounting. Because isOpen is never
// false, Modal's `if (!isOpen) return null` can never fire here — so
// dropping the call-site guard would not hide anything, it would leave a
// permanently mounted, permanently VISIBLE modal. The guard must stay.
const JobDetailsModal = ({
  setShowJobDetails,
  selectedJobForDetails,
  runDiagnosis,
  viewExistingDiagnosis
}) => (
        <Modal
          isOpen
          onClose={() => setShowJobDetails(false)}
          title="Job Details"
          size="lg"
        >
          <div className="space-y-6">
            {/* Job Header */}
            <div className="bg-info-tint rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-section-title font-semibold text-ink">{selectedJobForDetails.job_number}</h4>
                  <p className="text-sm text-ink-muted">{selectedJobForDetails.customer_name || 'No customer'}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedJobForDetails.job_status)}`}>
                    {selectedJobForDetails.job_status.replace('_', ' ')}
                  </span>
                  <div className="mt-1">
                    <span className={`text-sm font-medium ${getPriorityColor(selectedJobForDetails.priority)}`}>
                      {selectedJobForDetails.priority} priority
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div>
              <h5 className="text-md font-medium text-ink mb-3 flex items-center">
                <Car className="h-5 w-5 mr-2" />
                Vehicle Information
              </h5>
              <div className="bg-surface-page rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-ink-muted">Vehicle:</span>
                    <div className="font-medium text-ink">{selectedJobForDetails.vehicle_display}</div>
                  </div>
                  <div>
                    <span className="text-ink-muted">Mileage:</span>
                    <div className="font-medium text-ink">{selectedJobForDetails.mileage?.toLocaleString() || 'N/A'} miles</div>
                  </div>
                  {selectedJobForDetails.trim && (
                    <div>
                      <span className="text-ink-muted">Trim:</span>
                      <div className="font-medium text-ink">{selectedJobForDetails.trim}</div>
                    </div>
                  )}
                  {selectedJobForDetails.vin && (
                    <div>
                      <span className="text-ink-muted">VIN:</span>
                      <div className="font-mono text-ink">{selectedJobForDetails.vin}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Symptoms */}
            <div>
              <h5 className="text-md font-medium text-ink mb-3 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Symptoms & Description
              </h5>
              <div className="bg-surface-page rounded-lg p-4">
                <p className="text-ink">{selectedJobForDetails.symptom_description || selectedJobForDetails.symptom_summary || 'No symptoms described'}</p>
              </div>
            </div>

            {/* Diagnostic Summary (if available) */}
            {selectedJobForDetails.job_status === 'diagnosed' && (
              <div>
                <h5 className="text-md font-medium text-ink mb-3 flex items-center">
                  <Brain className="h-5 w-5 mr-2" />
                  Diagnostic Summary
                </h5>
                <div className="bg-success-tint rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-success">AI Analysis Complete</span>
                    <span className="text-small text-success">Ready for review</span>
                  </div>
                  <p className="text-sm text-success">
                    Click "View Results" to see detailed diagnostic analysis, suggested parts, and repair recommendations.
                  </p>
                </div>
              </div>
            )}

            {/* Photos */}
            <JobPhotos jobId={selectedJobForDetails.id} />

            {/* Timestamps */}
            <div>
              <h5 className="text-md font-medium text-ink mb-3 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Timeline
              </h5>
              <div className="bg-surface-page rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-ink-muted">Created:</span>
                    <div className="font-medium text-ink">
                      {new Date(selectedJobForDetails.created_at).toLocaleString()}
                    </div>
                  </div>
                  {selectedJobForDetails.updated_at && selectedJobForDetails.updated_at !== selectedJobForDetails.created_at && (
                    <div>
                      <span className="text-ink-muted">Last Updated:</span>
                      <div className="font-medium text-ink">
                        {new Date(selectedJobForDetails.updated_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="-mx-6 -mb-4 mt-2 px-6 py-4 border-t border-border-default bg-surface-page flex justify-end space-x-3">
            <button
              onClick={() => setShowJobDetails(false)}
              className="px-4 py-2 border border-border-default rounded-lg text-ink hover:bg-surface-sunken transition-colors"
            >
              Close
            </button>
            {(selectedJobForDetails.job_status === 'created' || selectedJobForDetails.job_status === 'diagnosing') && (
              <button
                onClick={() => {
                  setShowJobDetails(false);
                  runDiagnosis(selectedJobForDetails);
                }}
                className="px-4 py-2 bg-info text-white rounded-lg hover:bg-info/80 transition-colors flex items-center"
              >
                <Brain className="h-4 w-4 mr-2" />
                Run Diagnosis
              </button>
            )}
            {selectedJobForDetails.job_status === 'diagnosed' && (
              <button
                onClick={() => viewExistingDiagnosis(selectedJobForDetails)}
                className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/80 transition-colors flex items-center"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Results
              </button>
            )}
          </div>
        </Modal>
);

export default JobDetailsModal;
